import { RangeDatePicker } from '../../components/dashboard/DateFilter/RangeDatePicker';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FiAlertCircle,
    FiArrowDown,
    FiArrowUp,
    FiMinusCircle,
    FiPlusCircle,
    FiDownload,
    FiFileText,
    FiFilter,
    FiPieChart,
    FiRefreshCw
} from 'react-icons/fi';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../services/api';
import styles from './Reports.module.css';
import { usePreferences, useAuth } from '../../contexts';

type ReportType =
    | 'monthly-summary'
    | 'expenses-by-category'
    | 'income-by-source'
    | 'cashflow'
    | 'income-by-filial'
    | 'expenses-by-filial';

interface ReportTypeOption {
    id: ReportType;
    name: string;
    description: string;
}

const EXPENSE_COLORS = ['#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2'];
const INCOME_COLORS  = ['#14532d', '#166534', '#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac', '#bbf7d0'];

const toFileSlug = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

// Compara nome de subcategoria ignorando acento, caixa e espaço em excesso.
// Usado só para saber se a subcategoria do plano de contas tem lançamento.
const normalizeNome = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

export const Reports = () => {
    const [selectedType, setSelectedType] = useState<ReportType>('monthly-summary');

    const handleTypeChange = (newType: ReportType) => {
        setReportData(null);
        setSelectedType(newType);
    };
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [datePickerKey, setDatePickerKey] = useState(0);
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [categoriaFilter, setCategoriaFilter] = useState('');
    const [subcategoriaFilter, setSubcategoriaFilter] = useState('');
    const [formaPagamentoFilter, setFormaPagamentoFilter] = useState('');
    const [filialFilter, setFilialFilter] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [reportData, setReportData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const [categorias, setCategorias] = useState<any[]>([]);
    const [subcategorias, setSubcategorias] = useState<any[]>([]);
    const [subcategoriasComLancamento, setSubcategoriasComLancamento] = useState<string[]>([]);
    const [formasPagamento, setFormasPagamento] = useState<string[]>([]);
    const [filiais, setFiliais] = useState<{ id: string; name: string }[]>([]);
    const { formatCurrency, t } = usePreferences();
    const { hasFeature } = useAuth();
    const temFiliais = hasFeature('filiais');

    const reportTypes = useMemo((): ReportTypeOption[] => [
        { id: 'monthly-summary', name: t('rel.type.monthlySummary'), description: t('rel.type.monthlySummaryDesc') },
        { id: 'expenses-by-category', name: t('rel.type.expByCategory'), description: t('rel.type.expByCategoryDesc') },
        { id: 'income-by-source', name: t('rel.type.incBySource'), description: t('rel.type.incBySourceDesc') },
        { id: 'cashflow', name: t('rel.type.cashflow'), description: t('rel.type.cashflowDesc') },
        // Os dois por filial so fazem sentido para quem tem a feature.
        ...(temFiliais ? [
            { id: 'income-by-filial' as const, name: t('rel.type.incByFilial'), description: t('rel.type.incByFilialDesc') },
            { id: 'expenses-by-filial' as const, name: t('rel.type.expByFilial'), description: t('rel.type.expByFilialDesc') },
        ] : []),
    ], [t, temFiliais]);

    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [cats, formas, filiaisData, anos, subsUsadas] = await Promise.all([
                    api.getCategorias(),
                    api.getLancamentosFormasPagamento(),
                    api.getMinhasFiliais(),
                    api.getLancamentosAnos(),
                    api.getLancamentosSubcategorias(),
                ]);
                setCategorias(cats);
                setFormasPagamento(formas);
                setFiliais(filiaisData);
                setSubcategoriasComLancamento(subsUsadas);
                const now = new Date().getFullYear();
                const earliest = anos.length > 0 ? Math.min(...anos) : 2010;
                const minYear = Math.min(earliest, 2010);
                const maxYear = now + 1;
                const range: number[] = [];
                for (let y = maxYear; y >= minYear; y--) range.push(y);
                setAvailableYears(range);
            } catch (err) {
                console.error('Erro ao carregar opções de filtro:', err);
            }
        };
        loadFilterOptions();
    }, []);

    useEffect(() => {
        if (!categoriaFilter) {
            setSubcategorias([]);
            setSubcategoriaFilter('');
            return;
        }
        const loadSubs = async () => {
            try {
                const cat = categorias.find(c => c.nome === categoriaFilter);
                if (!cat) return;
                const subs = await api.getSubCategorias({ categoriaId: cat.id });
                setSubcategorias(subs);
                setSubcategoriaFilter('');
            } catch (err) {
                console.error('Erro ao carregar subcategorias:', err);
            }
        };
        loadSubs();
    }, [categoriaFilter, categorias]);

    // Só oferece no filtro subcategoria que de fato tem lançamento. Sem isso o
    // usuário consegue escolher uma conta zerada do plano de contas e recebe
    // uma tela vazia sem entender o motivo.
    const subcategoriasDisponiveis = useMemo(() => {
        if (subcategoriasComLancamento.length === 0) return subcategorias;
        const usadas = new Set(subcategoriasComLancamento.map(normalizeNome));
        return subcategorias.filter((sub: any) => usadas.has(normalizeNome(sub.nome)));
    }, [subcategorias, subcategoriasComLancamento]);

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await api.getReport({
                type: selectedType,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                categoriaLabel: categoriaFilter || undefined,
                subcategoriaLabel: subcategoriaFilter || undefined,
                formaPagamento: formaPagamentoFilter || undefined,
                filialIds: filialFilter.length > 0 ? filialFilter : undefined,
            });
            setReportData(data);
        } catch (err: any) {
            console.error('Erro ao gerar relatório:', err);
            setError(err.message || t('rel.errGerar'));
            setReportData(null);
        } finally {
            setIsLoading(false);
        }
    }, [selectedType, startDate, endDate, categoriaFilter, subcategoriaFilter, formaPagamentoFilter, filialFilter, t]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleGenerateReport = useCallback(() => {
        fetchReport();
    }, [fetchReport]);

    const handleClear = useCallback(() => {
        setStartDate('');
        setEndDate('');
        setCategoriaFilter('');
        setSubcategoriaFilter('');
        setFormaPagamentoFilter('');
        setFilialFilter([]);
        setDatePickerKey(k => k + 1);
    }, []);

    const applyYearShortcut = useCallback((year: number) => {
        setStartDate(`${year}-01-01`);
        setEndDate(`${year}-12-31`);
        setDatePickerKey(k => k + 1);
    }, []);

    /** "Outros" vem do backend sem a contagem no nome, para poder ser traduzido aqui. */
    const topCategoryLabel = (item: any): string =>
        item?.isOthers ? t('rel.outrasCategorias', { n: String(item.othersCount ?? 0) }) : item?.name;

    const getExportRows = (): { headers: string[]; rows: any[][] } => {
        if (!reportData) return { headers: [], rows: [] };

        if (selectedType === 'monthly-summary') {
            const d = reportData.data;
            const headers = [t('rel.metrica'), t('rel.valor')];
            const rows: any[][] = [
                [t('rel.totalReceitas'), formatCurrency(d?.totalIncome ?? 0)],
                [t('rel.totalDespesas'), formatCurrency(d?.totalExpenses ?? 0)],
                [t('rel.resultadoLiquido'), formatCurrency(d?.netBalance ?? 0)],
                [t('rel.transacoes'), d?.transactionCount ?? 0],
            ];
            // Receitas e despesas saem em blocos separados, cada um com o seu
            // subtotal. O total único de antes somava as duas naturezas e não
            // representava nada, e o cabeçalho dizia "Categoria" sobre uma
            // lista que é de subcategorias.
            const topCats = d?.topCategories ?? [];
            const pushBlock = (titulo: string, itens: any[], subtotalLabel: string, subtotal: number) => {
                if (itens.length === 0) return;
                rows.push(['', '']);
                rows.push([titulo, t('rel.valor')]);
                itens.forEach((c: any) => rows.push([topCategoryLabel(c), formatCurrency(c.amount)]));
                rows.push([subtotalLabel, formatCurrency(subtotal)]);
            };

            pushBlock(
                t('rel.principaisReceitas'),
                topCats.filter((c: any) => c.categoria === 'RECEITA'),
                t('rel.subtotalReceitas'),
                d?.totalIncome ?? 0,
            );
            pushBlock(
                t('rel.principaisDespesas'),
                topCats.filter((c: any) => c.categoria === 'GASTOS'),
                t('rel.subtotalDespesas'),
                d?.totalExpenses ?? 0,
            );

            return { headers, rows };
        }

        if (selectedType === 'expenses-by-category') {
            const rows = (reportData.data ?? []).map((item: any) => [
                item.category,
                formatCurrency(item.amount),
                `${item.percentage}%`,
            ]);
            rows.push([t('rel.total'), formatCurrency(reportData.total ?? 0), '100%']);
            return {
                headers: [t('rel.categoria'), t('rel.valor'), t('rel.pctTotal')],
                rows,
            };
        }

        if (selectedType === 'income-by-source') {
            const rows = (reportData.data ?? []).map((item: any) => [
                item.source,
                formatCurrency(item.amount),
                `${item.percentage}%`,
            ]);
            rows.push([t('rel.total'), formatCurrency(reportData.total ?? 0), '100%']);
            return {
                headers: [t('rel.fonte'), t('rel.valor'), t('rel.pctTotal')],
                rows,
            };
        }

        if (selectedType === 'cashflow') {
            const rows = (reportData.data ?? []).map((item: any) => [
                item.date,
                formatCurrency(item.income),
                formatCurrency(item.expense),
                formatCurrency(item.balance),
            ]);
            rows.push([
                t('rel.total'),
                formatCurrency(reportData.summary?.totalIncome ?? 0),
                formatCurrency(reportData.summary?.totalExpense ?? 0),
                formatCurrency(reportData.summary?.finalBalance ?? 0),
            ]);
            return {
                headers: [t('common.periodo'), t('chart.receitas'), t('chart.despesas'), t('chart.resultado')],
                rows,
            };
        }

        if (selectedType === 'income-by-filial' || selectedType === 'expenses-by-filial') {
            const rows = (reportData.data ?? []).map((item: any) => [
                item.filial,
                formatCurrency(item.amount),
                `${item.percentage}%`,
            ]);
            rows.push([t('rel.total'), formatCurrency(reportData.total ?? 0), '100%']);
            return {
                headers: [t('lanc.filial'), t('rel.valor'), t('rel.pctTotal')],
                rows,
            };
        }

        return { headers: [], rows: [] };
    };

    const handleExportExcel = useCallback(() => {
        if (!reportData) return;
        setIsExporting(true);
        try {
            const { headers, rows } = getExportRows();
            const reportName = reportTypes.find(r => r.id === selectedType)?.name ?? selectedType;
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, reportName.slice(0, 31));
            XLSX.writeFile(wb, `relatorio_${toFileSlug(reportName)}_${startDate}_${endDate}.xlsx`);
        } catch (err: any) {
            setError(`${t('rel.errGerar')}: ${err.message}`);
        } finally {
            setIsExporting(false);
        }
    }, [reportData, selectedType, startDate, endDate, reportTypes, t]);

    const handleExportPdf = useCallback(() => {
        if (!reportData) return;
        setIsExporting(true);
        try {
            const { headers, rows } = getExportRows();
            const reportName = reportTypes.find(r => r.id === selectedType)?.name ?? selectedType;
            const doc = new jsPDF();

            doc.setFillColor(1, 95, 89);
            doc.rect(0, 0, 220, 28, 'F');
            doc.setFontSize(16);
            doc.setTextColor(255, 255, 255);
            doc.text(reportName, 14, 14);
            doc.setFontSize(10);
            doc.text(`${t('common.periodo')}: ${startDate ? startDate : 'Início'} ${t('common.ate')} ${endDate ? endDate : 'Hoje'}`, 14, 22);

            doc.setTextColor(0, 0, 0);
            // Linhas de fechamento destacadas: o "Total:" dos demais relatórios e os
            // dois subtotais por natureza do Resumo Mensal.
            const totalLabels = [t('rel.total'), t('rel.subtotalReceitas'), t('rel.subtotalDespesas')];
            autoTable(doc, {
                head: [headers],
                body: rows,
                startY: 34,
                styles: { fontSize: 9 },
                headStyles: { fillColor: [1, 95, 89], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [236, 253, 245] },
                didParseCell: (data: any) => {
                    if (data.section === 'body' && totalLabels.includes(data.row.raw[0])) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [209, 250, 229];
                        data.cell.styles.textColor = [1, 95, 89];
                    }
                },
            });

            const pageCount = (doc as any).internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFillColor(1, 95, 89);
                doc.rect(0, 282, 220, 15, 'F');
                doc.setFontSize(8);
                doc.setTextColor(255, 255, 255);
                doc.text('Financeiro Diário - Sistema de Gestão Financeira', 14, 289);
                doc.text(`${t('common.pagina')} ${i} ${t('common.de')} ${pageCount}`, 180, 289);
            }

            doc.save(`relatorio_${toFileSlug(reportName)}_${startDate}_${endDate}.pdf`);
        } catch (err: any) {
            setError(`${t('rel.errGerar')}: ${err.message}`);
        } finally {
            setIsExporting(false);
        }
    }, [reportData, selectedType, startDate, endDate, reportTypes, t]);

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipLabel}>
                        {payload[0].name || payload[0].payload?.name || payload[0].payload?.category || payload[0].payload?.source || payload[0].payload?.filial}
                    </p>
                    <p className={styles.tooltipValue}>{formatCurrency(payload[0].value)}</p>
                </div>
            );
        }
        return null;
    };

    const CashflowTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipLabel}>{label}</p>
                    {payload.map((entry: any, i: number) => (
                        <p key={i} className={styles.tooltipValue} style={{ color: entry.fill }}>
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderSummaryCards = () => {
        if (selectedType !== 'monthly-summary' || !reportData?.data) return null;
        const { totalIncome, totalExpenses, netBalance, transactionCount, monthlyComparison } = reportData.data;
        return (
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.income}`}><FiPlusCircle size={20} /></div>
                    <div className={styles.statLabel}>{t('rel.totalReceitas')}</div>
                    <div className={`${styles.statValue} ${styles.income}`}>{formatCurrency(totalIncome ?? 0)}</div>
                    {monthlyComparison && (
                        <div className={`${styles.statChange} ${monthlyComparison.incomeChange >= 0 ? styles.positive : styles.negative}`}>
                            {monthlyComparison.incomeChange >= 0 ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                            {Math.abs(monthlyComparison.incomeChange)}% {t('rel.vsPeriodo')}
                        </div>
                    )}
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.expense}`}><FiMinusCircle size={20} /></div>
                    <div className={styles.statLabel}>{t('rel.totalDespesas')}</div>
                    <div className={`${styles.statValue} ${styles.expense}`}>{formatCurrency(totalExpenses ?? 0)}</div>
                    {monthlyComparison && (
                        <div className={`${styles.statChange} ${monthlyComparison.expenseChange <= 0 ? styles.positive : styles.negative}`}>
                            {monthlyComparison.expenseChange >= 0 ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                            {Math.abs(monthlyComparison.expenseChange)}% {t('rel.vsPeriodo')}
                        </div>
                    )}
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.balance}`}><FiPieChart size={20} /></div>
                    <div className={styles.statLabel}>{t('rel.resultadoLiquido')}</div>
                    <div className={styles.statValue}>{formatCurrency(netBalance ?? 0)}</div>
                    {monthlyComparison && (
                        <div className={`${styles.statChange} ${monthlyComparison.balanceChange >= 0 ? styles.positive : styles.negative}`}>
                            {monthlyComparison.balanceChange >= 0 ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                            {Math.abs(monthlyComparison.balanceChange)}% {t('rel.vsPeriodo')}
                        </div>
                    )}
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.transactions}`}><FiFileText size={20} /></div>
                    <div className={styles.statLabel}>{t('rel.transacoes')}</div>
                    <div className={styles.statValue}>{transactionCount ?? 0}</div>
                </div>
            </div>
        );
    };

    // O backend só manda diagnostics quando o resultado vem vazio. A ideia é
    // dizer o motivo em vez de deixar o usuário achando que perdeu lançamento.
    const renderEmptyState = () => {
        const foraDoPeriodo: number = reportData?.diagnostics?.lancamentosForaDoPeriodo ?? 0;
        const sugestoes: any[] = reportData?.diagnostics?.sugestoes ?? [];

        return (
            <div className={styles.emptyState}>
                <div>{t('rel.nenhumDado')}</div>

                {foraDoPeriodo > 0 && (
                    <div className={styles.emptyHint}>
                        {t('rel.dicaForaPeriodo', { n: String(foraDoPeriodo) })}
                    </div>
                )}

                {sugestoes.length > 0 && (
                    <div className={styles.emptyHint}>
                        <div>{t('rel.dicaNomeParecido')}</div>
                        <ul className={styles.emptySuggestions}>
                            {sugestoes.map((s: any) => (
                                <li key={s.subCategoria}>
                                    <strong>{s.subCategoria}</strong>
                                    {s.categoria ? ` (${s.categoria})` : ''}
                                    {' - '}
                                    {t('rel.dicaQtdLancamentos', { n: String(s.quantidade) })}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderCharts = () => {
        if (!reportData) return null;

        if (selectedType === 'income-by-filial' || selectedType === 'expenses-by-filial') {
            const data = reportData.data;
            if (!data || data.length === 0) return renderEmptyState();
            const filialColors = selectedType === 'income-by-filial' ? INCOME_COLORS : EXPENSE_COLORS;
            return (
                <div className={styles.reportsGrid}>
                    <div className={styles.reportCard}>
                        <h3>{reportData.title} <span>{t('rel.distribuicao')}</span></h3>
                        <div className={styles.chartContainer}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="amount" nameKey="filial">
                                        {data.map((_: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={filialColors[index % filialColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className={styles.totalLabel}>
                            {t('rel.total')} <strong>{formatCurrency(reportData.total ?? 0)}</strong>
                        </div>
                    </div>
                    <div className={styles.reportCard}>
                        <h3>{t('rel.detalhamentoPorFilial')} <span>{t('rel.pctTotal')}</span></h3>
                        <div className={styles.categoryList}>
                            {data.map((item: any, index: number) => (
                                <div key={index} className={styles.categoryItem}>
                                    <div className={styles.categoryColor} style={{ backgroundColor: filialColors[index % filialColors.length] }} />
                                    <div className={styles.categoryInfo}>
                                        <div className={styles.categoryName}>{item.filial}</div>
                                        <div className={styles.categoryBar}>
                                            <div className={styles.categoryProgress} style={{ width: `${item.percentage}%`, backgroundColor: filialColors[index % filialColors.length] }} />
                                        </div>
                                    </div>
                                    <div className={styles.categoryValues}>
                                        <div className={styles.categoryValue}>{formatCurrency(item.amount)}</div>
                                        <div className={styles.categoryPercent}>{item.percentage}%</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (selectedType === 'expenses-by-category' || selectedType === 'income-by-source') {
            const data = reportData.data;
            if (!data || data.length === 0) return renderEmptyState();
            const labelKey = selectedType === 'expenses-by-category' ? 'category' : 'source';
            const categoryColors = selectedType === 'income-by-source' ? INCOME_COLORS : EXPENSE_COLORS;
            // Use pieData (parent categories) for the pie chart; fall back to data if unavailable
            const pieData: any[] = reportData.pieData?.length ? reportData.pieData : data;
            return (
                <div className={styles.reportsGrid}>
                    <div className={styles.reportCard}>
                        <h3>{reportData.title} <span>{t('rel.distribuicao')}</span></h3>
                        <div className={styles.chartContainer}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="amount" nameKey={labelKey}>
                                        {pieData.map((_: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={categoryColors[index % categoryColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className={styles.totalLabel}>
                            {t('rel.total')} <strong>{formatCurrency(reportData.total ?? 0)}</strong>
                        </div>
                    </div>
                    <div className={styles.reportCard}>
                        <h3>{t('rel.detalhamento')} <span>{t('rel.pctTotal')}</span></h3>
                        <div className={styles.categoryList}>
                            {data.map((item: any, index: number) => (
                                <div key={index} className={styles.categoryItem}>
                                    <div className={styles.categoryColor} style={{ backgroundColor: categoryColors[index % categoryColors.length] }} />
                                    <div className={styles.categoryInfo}>
                                        <div className={styles.categoryName}>{item[labelKey]}</div>
                                        <div className={styles.categoryBar}>
                                            <div className={styles.categoryProgress} style={{ width: `${item.percentage}%`, backgroundColor: categoryColors[index % categoryColors.length] }} />
                                        </div>
                                    </div>
                                    <div className={styles.categoryValues}>
                                        <div className={styles.categoryValue}>{formatCurrency(item.amount)}</div>
                                        <div className={styles.categoryPercent}>{item.percentage}%</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (selectedType === 'cashflow') {
            const data = reportData.data;
            if (!data || data.length === 0) return renderEmptyState();
            return (
                <div className={styles.reportsGrid}>
                    <div className={`${styles.reportCard} ${styles.fullWidthCard}`}>
                        <h3>{t('rel.evolucaoFluxo')} <span>{t('rel.porPeriodo')}</span></h3>
                        <div className={styles.chartContainer}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                                    <Tooltip content={<CashflowTooltip />} />
                                    <Legend />
                                    <Bar dataKey="income" name={t('chart.receitas')} fill="#22c55e" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="expense" name={t('chart.despesas')} fill="#ef4444" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className={styles.reportCard}>
                        <h3>{t('rel.resumoPeriodo')}</h3>
                        <div className={styles.summaryList}>
                            <div className={styles.summaryItem}>
                                <span>{t('rel.totalReceitas')}</span>
                                <strong className={styles.income}>{formatCurrency(reportData.summary?.totalIncome ?? 0)}</strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span>{t('rel.totalDespesas')}</span>
                                <strong className={styles.expense}>{formatCurrency(reportData.summary?.totalExpense ?? 0)}</strong>
                            </div>
                            <div className={`${styles.summaryItem} ${styles.highlight}`}>
                                <span>{t('rel.resultadoFinal')}</span>
                                <strong>{formatCurrency(reportData.summary?.finalBalance ?? 0)}</strong>
                            </div>
                        </div>
                    </div>
                    <div className={styles.reportCard}>
                        <h3>{t('rel.evolucaoResultado')}</h3>
                        <div className={styles.chartContainer} style={{ height: 200 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Line type="monotone" dataKey="balance" name={t('chart.resultado')} stroke="#047857" strokeWidth={2} dot={{ fill: '#047857' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            );
        }

        if (selectedType === 'monthly-summary') {
            const topCategories = reportData?.data?.topCategories || [];
            if (topCategories.length === 0) return null;

            const incomeCategories = topCategories.filter((c: any) => c.categoria === 'RECEITA');
            const expenseCategories = topCategories.filter((c: any) => c.categoria === 'GASTOS');
            // Total do período, não a soma do que está na tela: com o "Outros" os
            // dois coincidem, mas assim o rótulo nunca diverge do cartão do topo.
            const incomeTotal = reportData?.data?.totalIncome ?? 0;
            const expenseTotal = reportData?.data?.totalExpenses ?? 0;

            const renderSection = (categories: any[], colors: string[], total: number, title: string) => {
                if (categories.length === 0) return null;
                // O gráfico recebe o nome já traduzido para o tooltip do "Outros"
                // não divergir do rótulo mostrado na lista ao lado.
                const chartData = categories.map((c: any) => ({ ...c, name: topCategoryLabel(c) }));
                return (
                    <div className={styles.reportsGrid}>
                        <div className={styles.reportCard}>
                            <h3>{title} <span>{t('rel.distribuicao')}</span></h3>
                            <div className={styles.chartContainer}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="amount" nameKey="name">
                                            {chartData.map((_: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className={styles.totalLabel}>
                                {t('rel.total')} <strong>{formatCurrency(total)}</strong>
                            </div>
                        </div>
                        <div className={styles.reportCard}>
                            <h3>{t('rel.detalhamento')} <span>{t('rel.pctTotal')}</span></h3>
                            <div className={styles.categoryList}>
                                {categories.map((item: any, index: number) => (
                                    <div key={index} className={styles.categoryItem}>
                                        <div className={styles.categoryColor} style={{ backgroundColor: colors[index % colors.length] }} />
                                        <div className={styles.categoryInfo}>
                                            <div className={styles.categoryName}>{topCategoryLabel(item)}</div>
                                            <div className={styles.categoryBar}>
                                                <div className={styles.categoryProgress} style={{ width: `${item.percentage}%`, backgroundColor: colors[index % colors.length] }} />
                                            </div>
                                        </div>
                                        <div className={styles.categoryValues}>
                                            <div className={styles.categoryValue}>{formatCurrency(item.amount)}</div>
                                            <div className={styles.categoryPercent}>{item.percentage}%</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            };

            return (
                <>
                    {renderSection(incomeCategories, INCOME_COLORS, incomeTotal, t('rel.principaisReceitas'))}
                    {renderSection(expenseCategories, EXPENSE_COLORS, expenseTotal, t('rel.principaisDespesas'))}
                </>
            );
        }

        return null;
    };

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title={t('rel.titulo')} subtitle={t('rel.subtitulo')} />
                <div className={styles.content}>
                    <div className={styles.pageHeader}>
                        <div className={styles.headerActions}>
                            <Button variant="outline" onClick={handleExportExcel} disabled={isExporting || !reportData}>
                                <FiDownload size={16} style={{ marginRight: '8px' }} />
                                {t('rel.exportarExcel')}
                            </Button>
                            <Button onClick={handleExportPdf} disabled={isExporting || !reportData}>
                                <FiDownload size={16} style={{ marginRight: '8px' }} />
                                {t('rel.exportarPdf')}
                            </Button>
                        </div>
                    </div>

                    <Card className={styles.filtersCard}>
                        <div className={styles.filtersHeader}>
                            <FiFilter size={18} />
                            <span>{t('rel.filtros')}</span>
                        </div>
                        <div className={styles.filtersBody}>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.tipoRelatorio')}</label>
                                <SearchableSelect
                                    value={selectedType}
                                    onChange={(val) => handleTypeChange(val as ReportType)}
                                    options={reportTypes.map(rt => ({ value: rt.id, label: rt.name }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.dataInicio')} / {t('rel.dataFim')}</label>
                                <RangeDatePicker
                                    key={datePickerKey}
                                    initialStart={startDate}
                                    initialEnd={endDate}
                                    onChange={(start, end) => { setStartDate(start); setEndDate(end); }}
                                    onClear={() => { setStartDate(''); setEndDate(''); setDatePickerKey(k => k + 1); }}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.grupo')}</label>
                                <SearchableSelect
                                    value={categoriaFilter}
                                    onChange={setCategoriaFilter}
                                    emptyLabel={t('rel.todas')}
                                    options={categorias.map((cat: any) => ({
                                        value: cat.nome,
                                        label: t(cat.nome),
                                        prefix: (
                                            <span className={`${styles.tipoBadge} ${cat.tipo === 'ENTRADA' ? styles.tipoBadgeReceita : styles.tipoBadgeGastos}`}>
                                                {cat.tipo === 'ENTRADA' ? 'RECEITA' : 'DESPESA'}
                                            </span>
                                        ),
                                    }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.categoria')}</label>
                                <SearchableSelect
                                    value={subcategoriaFilter}
                                    onChange={setSubcategoriaFilter}
                                    emptyLabel={t('rel.todas')}
                                    disabled={!categoriaFilter}
                                    options={subcategoriasDisponiveis.map((sub: any) => ({ value: sub.nome, label: t(sub.nome) }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.formaPagamento')}</label>
                                <SearchableSelect
                                    value={formaPagamentoFilter}
                                    onChange={setFormaPagamentoFilter}
                                    emptyLabel={t('rel.todas')}
                                    options={formasPagamento.map((fp) => ({ value: fp, label: t(fp) }))}
                                />
                            </div>
                        </div>
                        <div className={styles.filtersFooter}>
                            {availableYears.length > 0 && (
                                <div className={styles.yearSelectWrapper}>
                                    <label className={styles.yearSelectLabel}>Atalho de ano</label>
                                    <SearchableSelect
                                        value={(() => {
                                            if (startDate && endDate) {
                                                const y = startDate.slice(0, 4);
                                                if (startDate === `${y}-01-01` && endDate === `${y}-12-31`) return y;
                                            }
                                            return '';
                                        })()}
                                        onChange={(val) => {
                                            if (val) {
                                                applyYearShortcut(parseInt(val));
                                            } else {
                                                setStartDate('');
                                                setEndDate('');
                                                setDatePickerKey(k => k + 1);
                                            }
                                        }}
                                        emptyLabel="Todos os anos"
                                        options={availableYears.map(year => ({ value: String(year), label: String(year) }))}
                                    />
                                </div>
                            )}
                            <div className={styles.filterActions}>
                                <Button onClick={handleGenerateReport} disabled={isLoading}>
                                    <FiRefreshCw size={16} className={isLoading ? styles.spinning : ''} />
                                    {t('rel.gerarRelatorio')}
                                </Button>
                                <button type="button" className={styles.clearFiltersBtn} onClick={handleClear}>
                                    Limpar filtros
                                </button>
                            </div>
                        </div>
                        {temFiliais && filiais.length > 1 && (
                            <div className={styles.filialFilterRow}>
                                <span className={styles.filialFilterLabel}>{t('rel.filiais')}</span>
                                <div className={styles.filialChips}>
                                    {filiais.map(f => (
                                        <label
                                            key={f.id}
                                            className={`${styles.filialChip} ${filialFilter.includes(f.id) ? styles.filialChipActive : ''}`}
                                            title={f.name}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={filialFilter.includes(f.id)}
                                                onChange={(e) => {
                                                    setFilialFilter(prev =>
                                                        e.target.checked
                                                            ? [...prev, f.id]
                                                            : prev.filter(id => id !== f.id)
                                                    );
                                                }}
                                            />
                                            <span>{f.name}</span>
                                        </label>
                                    ))}
                                </div>
                                {filialFilter.length > 0 && (
                                    <button
                                        type="button"
                                        className={styles.clearFilter}
                                        onClick={() => setFilialFilter([])}
                                    >
                                        {t('rel.limpar', { n: String(filialFilter.length) })}
                                    </button>
                                )}
                            </div>
                        )}
                        <div className={styles.selectedReportInfo}>
                            <strong>{reportTypes.find(r => r.id === selectedType)?.name}</strong>
                            <span>{reportTypes.find(r => r.id === selectedType)?.description}</span>
                        </div>
                    </Card>

                    {error && (
                        <div className={styles.errorMessage}>
                            <FiAlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    {isLoading ? (
                        <div className={styles.loading}>
                            <FiRefreshCw size={32} className={styles.spinning} />
                            <p>{t('rel.gerandoRelatorio')}</p>
                        </div>
                    ) : (
                        <>
                            {renderSummaryCards()}
                            {renderCharts()}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};